import { finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ElementRef } from '@angular/core';
import { markInvalidFormControls } from '@core/utils';
import { ButtonComponent } from '@shared/components/ui';

@UntilDestroy({ checkProperties: true })
export class FormService {
  error: string;
  public form: FormGroup;
  isLoading: boolean;
  roles: string[];
  disabled: boolean;

  isNumeric(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  trackByFunction(index: any, item: any) {
    return item?.value?.id ?? item?.name ?? item?.id ?? index?.id;
  }

  async updateAndValidate(form: FormGroup | FormArray, data: any | any[], initiateForm?: FormGroup, emitEvent = true): Promise<FormGroup | FormArray> {
    if (data && Array.isArray(data) && Array.isArray(form?.controls)) {
      for (let index = 0; index < data.length; index++) {
        const element = data[index];
        form.controls[index] = await this._assignValuesToForm(element, form.controls[index] as FormGroup, initiateForm, emitEvent);
      }
    } else {
      form = await this._assignValuesToForm(data, form as FormGroup, initiateForm);
    }
    form.updateValueAndValidity();
    return form;
  }

  markGroupDirty(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      switch (formGroup.get(key).constructor.name) {
        case 'FormGroup':
          this.markGroupDirty(formGroup.get(key) as FormGroup);
          break;
        case 'FormArray':
          this.markArrayDirty(formGroup.get(key) as FormArray);
          break;
        case 'FormControl':
          this.markControlDirty(formGroup.get(key) as FormControl);
          break;
      }
    });
  }

  markArrayDirty(formArray: FormArray) {
    formArray.controls.forEach((control) => {
      switch (control.constructor.name) {
        case 'FormGroup':
          this.markGroupDirty(control as FormGroup);
          break;
        case 'FormArray':
          this.markArrayDirty(control as FormArray);
          break;
        case 'FormControl':
          this.markControlDirty(control as FormControl);
          break;
      }
    });
  }

  markAllInvalidControlsAndScrollToFirst(form: FormGroup, elRef?: ElementRef) {
    markInvalidFormControls(form, elRef);
  }

  markControlDirty(formControl: FormControl | AbstractControl) {
    formControl.markAsDirty();
  }

  changeBooleanToOpposite(obj: any) {
    return JSON.parse(JSON.stringify(obj), (key, val) => (typeof val === 'boolean' ? (obj[key] = !val) : val));
  }

  updateEnumValueToBooleanInForm(form: FormGroup, keyName: string, enumValue: any, value: any) {
    if (form.get(keyName)) {
      form.get(keyName).setValue(enumValue === value);
    }
  }

  protected updateRawValuesInForm(form: FormGroup, data: any, keysToReplaceArray: string[]): void {
    keysToReplaceArray.forEach((key) => {
      if (form.get(key) && Object.prototype.hasOwnProperty.call(data, key)) {
        form.get(key).setValue(data[key]);
      }
    });
  }

  protected removeNullObjects(data?: any): any {
    if (Array.isArray(data)) {
      // If it's an array, map over the elements and apply the function recursively
      return data.map((item) => this.removeNullObjects(item));
    } else if (typeof data === 'object' && data !== null) {
      // If it's an object, iterate over its properties
      Object.keys(data).forEach((key) => {
        if (data[key] == null || data[key] === '') {
          // Delete null or empty string properties
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete data[key];
        } else if (typeof data[key] === 'object') {
          // If the property is an object or array, recurse
          data[key] = this.removeNullObjects(data[key]);
        }
      });
      return data;
    } else {
      // For non-object/non-array types, return the data as is
      return data;
    }
  }

  protected async call(service$: any, buttonRef?: ButtonComponent): Promise<any> {
    buttonRef?.showLoading();
    this.isLoading = true;
    this.error = null;
    this.form?.disable({ emitEvent: false });
    return service$
      .pipe(
        finalize(() => {
          this.form?.markAsPristine();
          this.form?.enable({ emitEvent: false });
          buttonRef?.showSuccess();
          this.isLoading = false;
        }),
        untilDestroyed(this),
      )
      .toPromise()
      .catch((error: any) => {
        buttonRef?.showError();
        this.error = error?.error?.message || null;
        this.isLoading = false;
        throw error;
      });
  }

  protected removeEventFields(entity: any) {
    delete entity?.id;
    delete entity?.approvedBy;
    delete entity?.approvedOn;
    delete entity?.completedBy;
    delete entity?.completedOn;
    delete entity?.converted;
    delete entity?.convertedBy;
    delete entity?.convertedOn;
    delete entity?.createdBy;
    delete entity?.createdOn;
    delete entity?.deleted;
    delete entity?.deletedBy;
    delete entity?.deletedOn;
    delete entity?.modifiedBy;
    delete entity?.modifiedOn;
    delete entity?.rejectedBy;
    delete entity?.rejectedOn;
    delete entity?.sent;
    delete entity?.sentBy;
    delete entity?.sentOn;
    delete entity?.bankData;
    delete entity?.image;
    delete entity?.file;
    return entity;
  }

  protected updateEntityInstance<T>(instance: T, data: any): T {
    Object.keys(data).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(instance, key)) {
        instance[key] = data[key];
      }
    });
    return instance;
  }

  protected addTagFn(name: string) {
    return name.trim();
  }

  protected jsonToFromData(data: any, stringifyProperties?: string[]): FormData {
    const formData = new FormData();
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (stringifyProperties && stringifyProperties.includes(key)) {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    }
    return formData;
  }

  protected parseBoolean(value: string | boolean): boolean {
    return value === 'true' || value === true;
  }

  private async _assignValuesToForm(data: any, form: FormGroup, initiateForm?: FormGroup, emitEvent = true): Promise<FormGroup> {
    const _form: FormGroup = form ?? initiateForm;
    for (const property in data) {
      if (Object.prototype.hasOwnProperty.call(data, property) && _form.get(property) && (data as any)[property] != null) {
        if (typeof (data as any)[property] === 'boolean') {
          _form.get(property)?.setValue((data as any)[property], {
            emitEvent,
          });
        } else if (this._isDate((data as any)[property])) {
          const _date = new Date((data as any)[property])?.toISOString();
          _form.get(property)?.setValue(_date?.split('T')[0], {
            emitEvent,
          });
        } else {
          if (typeof (data as any)[property] === 'object') {
            // child object loop
            const childData = (data as any)[property];

            let isStringArray: boolean;
            if (Array.isArray(childData)) {
              isStringArray = childData?.some((value: any) => {
                return typeof value === 'string';
              });
            }
            if (isStringArray) {
              _form.get(property)?.setValue((data as any)[property], {
                emitEvent,
              });
            } else {
              for (const childProperty in childData) {
                if (Object.prototype.hasOwnProperty.call(childData, childProperty) && _form.get(`${property}.${childProperty}`) && (childData as any)[childProperty] != null) {
                  if (typeof (childData as any)[childProperty] === 'object') {
                    _form
                      .get(property)
                      ?.get(childProperty)
                      ?.setValue((childData as any)[childProperty], {
                        emitEvent,
                      });
                  } else {
                    if (typeof (childData as any)[childProperty] === 'boolean') {
                      _form.get(`${property}.${childProperty}`)?.setValue((childData as any)[childProperty], {
                        emitEvent,
                      });
                    } else {
                      _form.get(`${property}.${childProperty}`)?.setValue((childData as any)[childProperty], {
                        emitEvent,
                      });
                    }
                  }
                }
              }
            }

            // end
          } else {
            _form.get(property)?.setValue((data as any)[property], {
              emitEvent,
            });
          }
        }
      }
    }

    return _form;
  }

  private _isDate(_date: string) {
    const _regExp = new RegExp('^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$');
    return _regExp.test(_date);
  }
}
